import { useEffect, useMemo, useState } from "react";

interface PaginationResult<T> {
  /** Current 1-based page number. */
  page: number;
  /** Set the current page (1-based). */
  setPage: (page: number) => void;
  /** The slice of items for the current page. */
  pageItems: T[];
  /** Total number of pages (>= 1). */
  totalPages: number;
}

/**
 * Client-side pagination over an in-memory array.
 *
 * Pages are 1-based. The total page count scales with the data
 * (ceil(items / pageSize)), so a short list simply yields fewer pages. When the
 * list shrinks (e.g. after deletes) so the current page no longer exists, the
 * page is clamped back into range. In-place updates to existing items (same
 * length, same order) do not change the page, so background refreshes/polling
 * don't disrupt the user's position.
 */
export const usePagination = <T,>(items: T[], pageSize: number): PaginationResult<T> => {
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));

  // Clamp the page if the data shrank below the current page's range.
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  return { page, setPage, pageItems, totalPages };
};
